import React, { useState, useRef, useEffect } from "react";
import { buildAssetUrl } from "../utils/api";
import {
  BedDouble,
  Bath,
  Search,
  SlidersHorizontal,
  ArrowUpDown,
  ChevronRight,
  Minimize2,
  Maximize2,
  Move,
  X,
  Filter,
  DollarSign,
  Home,
  MapPin,
} from "lucide-react";
import ModalPropiedad from "./ModalPropiedad";

export default function SidebarPropiedad({
  propiedadesFiltradas,
  onOpenFiltros,
  filtrosAplicados,
  ordenActual,
  onSeleccionar,
  onDeseleccionarPropiedad,
  onCentrarPropiedad,
  onVerPropiedad,
  onCambiarOrden,
  onBuscar,
  busqueda,
  operacionSeleccionada,
  onCambiarOperacion,
  isMinimized = false,
  onToggleMinimize,
  mostrarModal,
  propiedadSeleccionada,
}) {
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const [width, setWidth] = useState(380);
  const [height, setHeight] = useState(840);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 24, y: 96 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isMaximized, setIsMaximized] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const sidebarRef = useRef(null);
  const dragHandleRef = useRef(null);
  const searchInputRef = useRef(null);
  const onToggleMinimizeRef = useRef(onToggleMinimize);
  const minWidth = 300;
  const maxWidth = 900;
  const minHeight = 400;
  const maxHeight = 900;

  // Detectar si es móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    onToggleMinimizeRef.current = onToggleMinimize;
  }, [onToggleMinimize]);

  // Guardar en localStorage solo en desktop
  useEffect(() => {
    if (isMobile) return;

    const savedWidth = localStorage.getItem("sidebar-width");
    const savedHeight = localStorage.getItem("sidebar-height");
    const savedPosition = localStorage.getItem("sidebar-position");
    const savedMinimized = localStorage.getItem("sidebar-minimized");

    if (savedWidth) setWidth(parseInt(savedWidth));
    if (savedHeight) setHeight(parseInt(savedHeight));
    if (savedPosition) setPosition(JSON.parse(savedPosition));
    if (savedMinimized === "true") {
      onToggleMinimizeRef.current?.();
    }
  }, [isMobile]);

  // Guardar cambios solo en desktop
  useEffect(() => {
    if (isMobile) return;

    localStorage.setItem("sidebar-width", width.toString());
    localStorage.setItem("sidebar-height", height.toString());
    localStorage.setItem("sidebar-position", JSON.stringify(position));
    localStorage.setItem("sidebar-minimized", isMinimized.toString());
  }, [width, height, position, isMinimized, isMobile]);

  // Efecto para redimensionamiento (solo desktop)
  useEffect(() => {
    if (isMobile) return;

    const handleMouseMove = (e) => {
      if (isResizing && resizeDirection) {
        const rect = sidebarRef.current.getBoundingClientRect();

        if (resizeDirection === "horizontal" || resizeDirection === "both") {
          const newWidth = e.clientX - rect.left;
          if (newWidth >= minWidth && newWidth <= maxWidth) {
            setWidth(newWidth);
          }
        }

        if (resizeDirection === "vertical" || resizeDirection === "both") {
          const newHeight = e.clientY - rect.top;
          if (newHeight >= minHeight && newHeight <= maxHeight) {
            setHeight(newHeight);
          }
        }
      }

      if (isDragging) {
        const newX = e.clientX - dragStart.x;
        const newY = e.clientY - dragStart.y;

        const maxX = window.innerWidth - (isMinimized ? 70 : width);
        const maxY = window.innerHeight - (isMinimized ? 70 : 100);

        setPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY)),
        });
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setIsDragging(false);
      setResizeDirection(null);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };

    if (isResizing || isDragging) {
      document.body.style.userSelect = "none";
      document.body.style.cursor = isResizing
        ? getResizeCursor(resizeDirection)
        : "grabbing";
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [
    isResizing,
    resizeDirection,
    isDragging,
    dragStart,
    width,
    height,
    isMinimized,
    isMobile,
  ]);

  // Enfocar input al abrir búsqueda móvil
  useEffect(() => {
    if (isMobileSearchOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [isMobileSearchOpen]);

  const getResizeCursor = (direction) => {
    switch (direction) {
      case "horizontal":
        return "ew-resize";
      case "vertical":
        return "ns-resize";
      case "both":
        return "nwse-resize";
      default:
        return "default";
    }
  };

  const getPropiedadId = (propiedad) => propiedad?.id || propiedad?._id;

  const isPropiedadSeleccionada = (propiedad) => {
    if (!propiedadSeleccionada) return false;

    const seleccionadaId = getPropiedadId(propiedadSeleccionada);
    const propiedadId = getPropiedadId(propiedad);

    if (seleccionadaId && propiedadId) return seleccionadaId === propiedadId;

    return (
      propiedadSeleccionada.titulo === propiedad.titulo &&
      propiedadSeleccionada.ubicacion === propiedad.ubicacion
    );
  };

  const handleDragStart = (e) => {
    if (isMobile) return;

    if (dragHandleRef.current && dragHandleRef.current.contains(e.target)) {
      e.preventDefault();
      setIsDragging(true);
      const rect = sidebarRef.current.getBoundingClientRect();
      setDragStart({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  };

  const handleResizeStart = (e, direction) => {
    if (isMobile) return;

    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeDirection(direction);
  };

  const toggleMaximize = () => {
    if (isMobile) return;

    if (isMaximized) {
      setWidth(380);
      setHeight(840);
      setPosition({ x: 24, y: 96 });
    } else {
      setWidth(window.innerWidth * 0.8);
      setHeight(window.innerHeight * 0.85);
      setPosition({
        x: (window.innerWidth - window.innerWidth * 0.8) / 2,
        y: 40,
      });
    }
    setIsMaximized(!isMaximized);
  };

  const cabeceraConControles = ({ mostrarMaximizar = true } = {}) => (
    <div className="relative pt-8 pb-4 px-6 bg-gradient-to-b from-white/60 to-white/30 border-b border-white/30">
      <div className="grid grid-cols-3 items-center gap-3">
        <div className="justify-self-start text-xs text-slate-500 font-mono bg-white/60 px-2 py-1 rounded-lg border border-slate-200/50">
          {Math.round(width)} × {Math.round(height)}
        </div>

        <div
          ref={dragHandleRef}
          className="justify-self-center w-32 h-8 flex items-center cursor-move group"
        >
          <div className="w-full h-full flex flex-col items-center justify-center">
            <div className="w-24 h-3 bg-slate-300/60 rounded-full group-hover:bg-slate-400/70 transition-all duration-300 group-active:bg-blue-400/70 flex items-center justify-center">
              <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <Move size={10} className="text-slate-600" />
                <Move size={10} className="text-slate-600" />
                <Move size={10} className="text-slate-600" />
              </div>
            </div>
            <div className="text-[9px] text-slate-500/70 font-semibold mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
              Arrastrar para mover
            </div>
          </div>
        </div>

        <div className="justify-self-end flex items-center gap-2">
          {mostrarMaximizar && (
            <button
              onClick={toggleMaximize}
              className="w-8 h-8 flex items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-white/80 rounded-xl transition-all duration-200 border border-slate-200/50 hover:border-slate-300/70"
              title={isMaximized ? "Restaurar tamaño normal" : "Maximizar"}
            >
              {isMaximized ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
          )}

          <button
            onClick={onToggleMinimize}
            className="w-8 h-8 flex items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-white/80 rounded-xl transition-all duration-200 border border-slate-200/50 hover:border-slate-300/70"
            title="Minimizar completamente"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );

  const resizeHandles = () => (
    <>
      <div
        className="absolute bottom-0 right-0 w-6 h-6 cursor-nwse-resize group"
        onMouseDown={(e) => handleResizeStart(e, "both")}
      >
        <div className="absolute bottom-0.5 right-0.5 w-5 h-5 flex items-end justify-end">
          <div className="w-4 h-4 border-r-2 border-b-2 border-blue-400/50 rounded-br-lg group-hover:border-blue-500 group-active:border-blue-600 transition-colors"></div>
        </div>
      </div>

      <div
        className="absolute right-0 top-0 bottom-0 w-4 cursor-ew-resize group"
        onMouseDown={(e) => handleResizeStart(e, "horizontal")}
      >
        <div className="absolute top-1/2 right-1 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="flex flex-col gap-1">
            <div className="w-2 h-2 bg-blue-400/60 rounded-full"></div>
            <div className="w-2 h-2 bg-blue-400/60 rounded-full"></div>
            <div className="w-2 h-2 bg-blue-400/60 rounded-full"></div>
          </div>
        </div>
      </div>

      <div
        className="absolute bottom-0 left-0 right-0 h-4 cursor-ns-resize group"
        onMouseDown={(e) => handleResizeStart(e, "vertical")}
      >
        <div className="absolute bottom-1 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="flex gap-1">
            <div className="w-2 h-2 bg-blue-400/60 rounded-full"></div>
            <div className="w-2 h-2 bg-blue-400/60 rounded-full"></div>
            <div className="w-2 h-2 bg-blue-400/60 rounded-full"></div>
          </div>
        </div>
      </div>
    </>
  );

  const tarjetaPropiedad = (propiedad, index, c) => (
    <div
      key={index}
      onClick={() => onSeleccionar(propiedad)}
      className={`group relative rounded-2xl p-3 cursor-pointer transition-all duration-300 active:scale-[0.99] ${
        isPropiedadSeleccionada(propiedad)
          ? "bg-gradient-to-br from-blue-50 to-sky-50 border-2 border-blue-400 shadow-xl shadow-blue-100/70"
          : "bg-white/90 border border-slate-100 hover:shadow-xl hover:shadow-slate-200/50 hover:-translate-y-0.5 hover:border-slate-200"
      }`}
    >
      <div className="flex gap-4">
        <div className="w-24 h-24 shrink-0 rounded-xl overflow-hidden border border-slate-100">
          <img
            src={buildAssetUrl(propiedad.foto_principal)}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            alt={propiedad.titulo}
          />
        </div>

        <div className="flex flex-col justify-between py-1 flex-1">
          <div>
            <div className="flex justify-between items-start">
              <span className="text-sm font-bold text-slate-900">
                ${Number(propiedad.precio).toLocaleString()}
              </span>
              <span
                className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md ${
                  propiedad.tipo_operacion === "venta"
                    ? "bg-gradient-to-br from-green-50 to-green-100 text-green-700 border border-green-200"
                    : "bg-gradient-to-br from-blue-50 to-blue-100 text-blue-700 border border-blue-200"
                }`}
              >
                {propiedad.tipo_operacion}
              </span>
            </div>
            <p className="text-[11px] text-slate-500 line-clamp-1 mt-1 font-medium">
              {propiedad.ubicacion}
            </p>
          </div>

          <div className="flex gap-4 mt-2">
            <div className="flex items-center gap-1.5 text-slate-400">
              <BedDouble size={14} className="text-slate-300" />
              <span className="text-xs font-semibold text-slate-600">
                {c.recamaras || 0}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-400">
              <Bath size={14} className="text-slate-300" />
              <span className="text-xs font-semibold text-slate-600">
                {c.banos || 0}
              </span>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onCentrarPropiedad?.(propiedad);
              }}
              className="ml-auto mr-1 flex items-center gap-1.5 text-slate-400 transition-colors hover:text-blue-700"
              title="Centrar y acercar en el mapa"
            >
              <MapPin
                size={14}
                className="text-slate-300 group-hover:text-blue-400"
              />
              <span className="text-xs font-semibold text-slate-700">
                {propiedad.zona || "Zona"}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // MÓVIL: Componente minimizado tipo Facebook
  if (isMobile) {
    return (
      <>
        {propiedadSeleccionada && (
          <div className="fixed bottom-20 left-4 right-4 z-40">
            <div className="relative flex items-center gap-3 rounded-2xl border border-slate-200/80 bg-white/95 px-4 py-3 shadow-2xl backdrop-blur-xl">
              <button
                type="button"
                onClick={onDeseleccionarPropiedad}
                className="absolute -top-3 -right-3 flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-lg transition-colors hover:bg-slate-50 hover:text-slate-900 active:scale-95"
                title="Deseleccionar propiedad"
                aria-label="Deseleccionar propiedad"
              >
                <X size={16} />
              </button>

              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-600">
                  Propiedad seleccionada
                </p>
                <p className="truncate text-sm font-semibold text-slate-900">
                  {propiedadSeleccionada.ubicacion}
                </p>
              </div>

              <button
                type="button"
                onClick={onVerPropiedad}
                className="shrink-0 rounded-xl bg-blue-600 px-3.5 py-2 text-xs font-bold text-white shadow-lg shadow-blue-600/25 transition-colors hover:bg-blue-700 active:scale-95"
              >
                Ver propiedad
              </button>
            </div>
          </div>
        )}

        {/* Botón flotante de búsqueda tipo Facebook */}
        {!isMobileSearchOpen && (
          <div className="fixed top-4 right-4 z-50">
            <button
              onClick={() => setIsMobileSearchOpen(true)}
              className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-2xl flex items-center justify-center shadow-2xl hover:from-blue-600 hover:to-blue-700 transition-all duration-300 hover:scale-105 active:scale-95 border-2 border-white/30 backdrop-blur-sm"
              title="Buscar propiedades"
            >
              <Search size={22} />
            </button>

            {/* Badge de resultados si hay filtros */}
            {filtrosAplicados && (
              <div className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold border-2 border-white">
                {propiedadesFiltradas?.length || 0}
              </div>
            )}
          </div>
        )}

        {/* Overlay de búsqueda móvil */}
        {isMobileSearchOpen && (
          <div className="fixed inset-0 z-50">
            {/* Fondo con blur */}
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setIsMobileSearchOpen(false)}
            />

            {/* Panel de búsqueda */}
            <div className="absolute top-0 right-0 left-0 bg-gradient-to-b from-white to-slate-50 rounded-b-3xl shadow-2xl animate-in slide-in-from-top duration-300">
              {/* Cabecera */}
              <div className="p-4 border-b border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-slate-800">
                    Buscar Propiedades
                  </h2>
                  <button
                    onClick={() => setIsMobileSearchOpen(false)}
                    className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center text-slate-600 hover:bg-slate-200 transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>

                {/* Búsqueda principal */}
                <div className="relative mb-3">
                  <Search
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    size={20}
                  />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Ubicación, colonia, zona..."
                    value={busqueda}
                    onChange={(e) => onBuscar(e.target.value)}
                    className="w-full bg-white border-2 border-slate-200 rounded-xl py-3 pl-12 pr-4 text-base focus:outline-none focus:border-blue-500"
                  />
                </div>

                {/* Filtros rápidos */}
                <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
                  <button
                    onClick={() => onCambiarOperacion("venta")}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap ${operacionSeleccionada === "venta" ? "bg-green-100 text-green-700 border border-green-300" : "bg-slate-100 text-slate-700 border border-slate-200"}`}
                  >
                    <DollarSign size={16} />
                    Venta
                  </button>
                  <button
                    onClick={() => onCambiarOperacion("renta")}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap ${operacionSeleccionada === "renta" ? "bg-blue-100 text-blue-700 border border-blue-300" : "bg-slate-100 text-slate-700 border border-slate-200"}`}
                  >
                    <Home size={16} />
                    Renta
                  </button>
                  <button
                    onClick={onOpenFiltros}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap bg-orange-100 text-orange-700 border border-orange-300"
                  >
                    <Filter size={16} />
                    Filtros
                    {filtrosAplicados && (
                      <span className="w-5 h-5 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center">
                        ✓
                      </span>
                    )}
                  </button>
                </div>
              </div>

              {/* Resultados */}
              <div className="max-h-[60vh] overflow-y-auto p-4">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-semibold text-slate-600">
                    {propiedadesFiltradas?.length || 0} propiedades encontradas
                  </span>
                  <select
                    value={ordenActual}
                    onChange={(e) => onCambiarOrden(e.target.value)}
                    className="text-sm bg-transparent text-slate-600 focus:outline-none"
                  >
                    <option value="precio_desc">Mayor precio</option>
                    <option value="precio_asc">Menor precio</option>
                  </select>
                </div>

                {/* Lista de resultados */}
                <div className="space-y-3">
                  {(propiedadesFiltradas || [])
                    .slice(0, 5)
                    .map((propiedad, index) => {
                      const c = propiedad.caracteristicas || {};
                      return (
                        <div
                          key={index}
                          onClick={() => {
                            onSeleccionar(propiedad);
                            setIsMobileSearchOpen(false);
                          }}
                          className="bg-white border border-slate-200 rounded-xl p-3 cursor-pointer active:scale-[0.98] transition-transform"
                        >
                          <div className="flex gap-3">
                            <div className="w-16 h-16 shrink-0 rounded-lg overflow-hidden border border-slate-100">
                              <img
                                src={buildAssetUrl(propiedad.foto_principal)}
                                className="w-full h-full object-cover"
                                alt={propiedad.titulo}
                              />
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-start">
                                <span className="text-sm font-bold text-slate-900 truncate">
                                  ${Number(propiedad.precio).toLocaleString()}
                                </span>
                                <span
                                  className={`text-[10px] font-bold px-2 py-0.5 rounded ${propiedad.tipo_operacion === "venta" ? "bg-green-50 text-green-700" : "bg-blue-50 text-blue-700"}`}
                                >
                                  {propiedad.tipo_operacion}
                                </span>
                              </div>
                              <p className="text-xs text-slate-500 truncate mt-1">
                                {propiedad.ubicacion}
                              </p>
                              <div className="flex gap-3 mt-2">
                                <div className="flex items-center gap-1 text-slate-500">
                                  <BedDouble size={14} />
                                  <span className="text-xs font-semibold">
                                    {c.recamaras || 0}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1 text-slate-500">
                                  <Bath size={14} />
                                  <span className="text-xs font-semibold">
                                    {c.banos || 0}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                  {/* Ver más resultados */}
                  {propiedadesFiltradas?.length > 5 && (
                    <button
                      onClick={() => {
                        setIsMobileSearchOpen(false);
                        // Aquí podrías mostrar más resultados en una vista completa
                      }}
                      className="w-full py-3 bg-slate-100 text-slate-700 rounded-xl text-sm font-semibold hover:bg-slate-200 transition-colors"
                    >
                      Ver {propiedadesFiltradas.length - 5} propiedades más...
                    </button>
                  )}

                  {propiedadesFiltradas?.length === 0 && (
                    <div className="text-center py-8">
                      <Search
                        size={32}
                        className="text-slate-300 mx-auto mb-3"
                      />
                      <p className="text-slate-500 font-medium">
                        No se encontraron propiedades
                      </p>
                      <p className="text-sm text-slate-400 mt-1">
                        Prueba con otros filtros
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // DESKTOP
  if (isMinimized) {
    return (
      <div
        ref={sidebarRef}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
        }}
        className="fixed z-50 w-[70px] h-[70px] bg-white/95 backdrop-blur-xl border-2 border-white/40 rounded-2xl shadow-2xl flex flex-col items-center justify-center cursor-move group select-none"
        onMouseDown={handleDragStart}
      >
        <div
          ref={dragHandleRef}
          className="absolute inset-0 cursor-move rounded-2xl flex items-center justify-center"
        >
          <div className="w-10 h-10 bg-blue-100/30 rounded-full flex items-center justify-center border-2 border-blue-200/40 group-hover:border-blue-300/60 transition-all">
            <Move
              size={20}
              className="text-blue-500/70 group-hover:text-blue-600"
            />
          </div>
        </div>

        <button
          onClick={onToggleMinimize}
          className="absolute -right-2 -top-2 w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-full flex items-center justify-center shadow-lg hover:from-blue-600 hover:to-blue-700 transition-all z-10 hover:scale-110 active:scale-95"
          title="Expandir panel"
        >
          <ChevronRight size={18} />
        </button>

        <div className="text-center mt-8">
          <div className="text-base font-bold text-blue-600 bg-white/80 px-2 py-1 rounded-lg">
            {propiedadesFiltradas?.length || 0}
          </div>
          <div className="text-[10px] text-slate-500 mt-1 font-semibold">
            Propiedades
          </div>
        </div>
      </div>
    );
  }

  if (propiedadSeleccionada) {
    return (
      <div
        ref={sidebarRef}
        style={{
          width: `${width}px`,
          height: `${height}px`,
          left: `${position.x}px`,
          top: `${position.y}px`,
          maxHeight: "90vh",
        }}
        className="fixed z-50 bg-white/95 backdrop-blur-xl border-2 border-white/40 rounded-[26px] shadow-2xl flex flex-col overflow-hidden select-none"
        onMouseDown={handleDragStart}
      >
        {cabeceraConControles()}

        <div className="flex-1 overflow-y-auto px-4 pb-4 custom-scrollbar space-y-4">
          <div className="relative group bg-gradient-to-br from-blue-50 to-sky-50 border-2 border-blue-400 shadow-xl shadow-blue-100/70 rounded-3xl p-4 pb-4">
            <button
              onClick={() => onDeseleccionarPropiedad?.()}
              className="absolute top-1 right-1 w-8 h-8 flex items-center justify-center rounded-full bg-white text-slate-500 shadow-lg border border-slate-200 hover:text-slate-800 hover:bg-slate-50 transition-all"
              title="Deseleccionar propiedad"
            >
              <X size={16} />
            </button>

            <div className="flex gap-4">
              <div className="flex-1 min-w-0 pr-8">
                <div className="flex justify-between items-start gap-3">
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-blue-700/70 mb-1">
                      Propiedad seleccionada
                    </p>
                  </div>
                </div>

                <p className="text-sm text-slate-600 line-clamp-2 mt-2">
                  {propiedadSeleccionada.ubicacion}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => onCentrarPropiedad?.(propiedadSeleccionada)}
              className="absolute bottom-2 right-1 flex items-center gap-1.5 text-slate-500 bg-white/80 px-2.5 py-1.5 rounded-xl border border-white/70 hover:bg-white hover:border-blue-200 hover:text-blue-700 transition-all shadow-sm"
              title="Centrar y acercar en el mapa"
            >
              <MapPin size={14} className="text-slate-400" />
            </button>
          </div>

          <div className="rounded-[28px] overflow-hidden border border-slate-100 bg-white shadow-lg">
            <ModalPropiedad
              propiedad={propiedadSeleccionada}
              onClose={() => onDeseleccionarPropiedad?.()}
              isModal={false}
              showCloseButton={false}
            />
          </div>

          {resizeHandles()}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={sidebarRef}
      style={{
        width: `${width}px`,
        height: `${height}px`,
        left: `${position.x}px`,
        top: `${position.y}px`,
        maxHeight: "90vh",
      }}
      className="fixed z-50 bg-white/95 backdrop-blur-xl border-2 border-white/40 rounded-[26px] shadow-2xl flex flex-col overflow-hidden select-none"
      onMouseDown={handleDragStart}
    >
      {cabeceraConControles({ mostrarMaximizar: true })}
      <div className="px-4 pb-4 space-y-3">
        {/* Contenido de búsqueda */}
        <div className="relative group">
          <Search
            className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-600 transition-colors"
            size={18}
          />
          <input
            type="text"
            placeholder="Buscar por ubicación..."
            value={busqueda}
            onChange={(e) => onBuscar(e.target.value)}
            className="w-full bg-white/80 border border-slate-200/70 rounded-2xl py-3 pl-12 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:bg-white transition-all placeholder:text-slate-400"
          />
        </div>

        <div className="flex gap-2">
          <select
            value={operacionSeleccionada}
            onChange={(e) => onCambiarOperacion(e.target.value)}
            className="flex-1 bg-white/80 border border-slate-200/70 rounded-xl px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-slate-600 focus:outline-none appearance-none cursor-pointer hover:bg-white transition-colors"
          >
            <option value="">Operación</option>
            <option value="venta">Venta</option>
            <option value="renta">Renta</option>
          </select>

          <button
            onClick={onOpenFiltros}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-widest transition-all ${
              filtrosAplicados
                ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-600/30"
                : "bg-white/80 text-slate-600 border border-slate-200/70 hover:bg-white"
            }`}
          >
            <SlidersHorizontal size={14} />
            {filtrosAplicados ? "Activos" : "Filtros"}
          </button>
        </div>
      </div>

      {/* Control de Ordenamiento */}
      <div className="px-4 py-3 flex justify-between items-center border-y border-slate-100/50 bg-slate-50/50">
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
          {propiedadesFiltradas?.length || 0} Resultados
        </span>
        <div className="flex items-center gap-2 text-slate-500">
          <ArrowUpDown size={12} />
          <select
            value={ordenActual}
            onChange={(e) => onCambiarOrden(e.target.value)}
            className="bg-transparent text-[10px] font-bold uppercase tracking-widest focus:outline-none cursor-pointer hover:text-slate-700"
          >
            <option value="precio_desc">Mayor Precio</option>
            <option value="precio_asc">Menor Precio</option>
            <option value="nombre_asc">Nombre A-Z</option>
            <option value="area_desc">Mayor Área</option>
          </select>
        </div>
      </div>

      {/* Lista Scrollable */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 custom-scrollbar">
        {(propiedadesFiltradas || []).map((propiedad, index) => {
          const c = propiedad.caracteristicas || {};
          return tarjetaPropiedad(propiedad, index, c);
        })}

        {propiedadesFiltradas?.length === 0 && (
          <div className="text-center py-10">
            <div className="w-16 h-16 bg-gradient-to-br from-slate-100/50 to-slate-200/50 rounded-full flex items-center justify-center mx-auto mb-4 border border-slate-200/50">
              <Search size={20} className="text-slate-400" />
            </div>
            <p className="text-sm font-serif italic text-slate-400">
              Sin coincidencias en este sector
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Prueba ajustando los filtros
            </p>
          </div>
        )}
      </div>

      {resizeHandles()}
    </div>
  );
}
